import { Button, Dropdown, Menu } from "antd";
import { useTranslation } from "react-i18next";

import {
  hasRepresentativeName,
  isRepresentativeAlreadyShareholder,
} from "./kapgRepresentativeCopy";
import type {
  TaxRegistrationRepresentative,
  TaxRegistrationShareholder,
} from "./types";

interface KapgRepresentativePickerProps {
  representatives: TaxRegistrationRepresentative[] | undefined;
  shareholders: TaxRegistrationShareholder[] | undefined;
  onPick: (index: number) => void;
}

/**
 * Lets the operator copy an already-entered Gesetzlicher Vertreter into an
 * Anteilseigner block (SMT-1359) instead of typing the same person twice. Only
 * offers representatives that carry a name and are not already a
 * natural-person shareholder somewhere on the form; renders nothing when none
 * qualify.
 */
export const KapgRepresentativePicker = ({
  representatives,
  shareholders,
  onPick,
}: KapgRepresentativePickerProps) => {
  const { t } = useTranslation("TaxRegistration");
  const candidates = (representatives ?? [])
    .map((representative, index) => ({ representative, index }))
    .filter(
      ({ representative }) =>
        hasRepresentativeName(representative) &&
        !isRepresentativeAlreadyShareholder(representative, shareholders)
    );

  if (candidates.length === 0) return null;

  const label = t("kapg.actions.copyRepresentative");

  if (candidates.length === 1) {
    const [{ index }] = candidates;
    return (
      <Button
        data-test="copy-representative"
        onClick={() => onPick(index)}
        type="link"
      >
        {label}
      </Button>
    );
  }

  const menu = (
    <Menu>
      {candidates.map(({ representative, index }) => (
        <Menu.Item key={index} onClick={() => onPick(index)}>
          {t("kapg.labels.representativeOption", {
            index: index + 1,
            name: `${representative.firstName ?? ""} ${
              representative.lastName ?? ""
            }`.trim(),
          })}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown overlay={menu} trigger={["click"]}>
      <Button data-test="copy-representative" type="link">
        {label}
      </Button>
    </Dropdown>
  );
};
